/* @flow */
import produce from 'immer';
import get from 'lodash/get';
import { message } from 'antd';
import LocalStorageService from '@/services/LocalStorageService';
import { actionTypes } from './actions';
import { defineServerErrors, defineUserPermissions } from '@/utils/redux';

const {
  LOG_IN,
  LOG_IN_SUCCESS,
  LOG_IN_ERROR,

  SIGN_UP,
  SIGN_UP_SUCCESS,
  SIGN_UP_ERROR,

  SIGN_OUT,
  SIGN_OUT_SUCCESS,
  SIGN_OUT_ERROR,

  CHECK_TOKEN,
  CHECK_TOKEN_SUCCESS,
  CHECK_TOKEN_FAILED,
  CANCEL_CHECK_TOKEN,

  UPDATE_USER,
  UPDATE_USER_SUCCESS,
  UPDATE_USER_FAILED,

  UPDATE_ORGANIZATION,
  UPDATE_ORGANIZATION_SUCCESS,
  UPDATE_ORGANIZATION_FAILED,

  SEND_EMAIL_RESET,
  SEND_EMAIL_RESET_SUCCESS,
  SEND_EMAIL_RESET_FAILED,

  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILED,

  FETCH_ORGANIZATIONS,
  FETCH_ORGANIZATIONS_SUCCESS,
  FETCH_ORGANIZATIONS_FAILED,

  AVATAR_UPLOAD,
  REMOVE_AVATAR,

  RESET_ERRORS,
  SET_TERMS_POLICY_PAYLOAD,

  ACCEPT_TERMS_AND_POLICY,
  ACCEPT_TERMS_AND_POLICY_SUCCESS,
  ACCEPT_TERMS_AND_POLICY_FAILED,

  SAVE_SUPPORT_INFO,
  SAVE_SUPPORT_INFO_SUCCESS,
  SAVE_SUPPORT_INFO_FAILED,
} = actionTypes;

type State = {
  isAuthorized: ?boolean,
  isFetching: boolean,
  isLogIn: boolean,
  isSignUp: boolean,
  isSaveSupportInfo: boolean,
  profile: ProfileT,
  loginError: ?string,
  registerErrors: Object,
  updateUserError: Object,
  autoLogin: boolean,
  permissions: ?Object,
  fetchUserErrors: string[],
  updateOrganizationError: string[],
  resetPasswordData: ?Object,
  resetPasswordError: string[],
  passwordResetMessage: string,
  organizations: Array<Object>,
  isOrganizationsFetching: boolean,
  termsPolicyPayload: {
    is_terms_accepted: boolean,
    privacy_policy_file: string,
    terms_of_service_file: string,
  } | null,
  acceptTermsAndPolicySuccess: ?boolean,
  acceptTermsAndPolicyLoading: boolean,
  saveSupportInfoStatus: string,
};

const initialState = {
  isAuthorized: null,
  isFetching: false,
  isLogIn: false,
  isSignUp: false,
  isSaveSupportInfo: false,
  profile: {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    user_type: 0,
    organization: {
      pk: 0,
      name: '',
      logo: '',
      description: '',
      membership_type: 0,
      business_type: 0,
    },
    token: '',
    image: null,
    is_terms_accepted: false,
    privacy_policy_file: '',
    terms_of_service_file: '',
  },
  loginError: null,
  registerErrors: {
    email: null,
    phone_number: null,
    non_field_errors: null,
  },
  updateUserError: {
    email: null,
    phone_number: null,
    non_field_errors: null,
  },
  autoLogin: true,
  permissions: {
    isAdminRole: false,
    viewerAdmin: false,
    admin: false,
    superAdmin: false,
    orgAdmin: false,
    salesAffiliate: false,
    EPCMember: false,
  },
  fetchUserErrors: [],
  updateOrganizationError: [],
  resetPasswordData: null,
  resetPasswordError: [],
  passwordResetMessage: '',
  organizations: [],
  isOrganizationsFetching: false,
  termsPolicyPayload: null,
  acceptTermsAndPolicySuccess: null,
  acceptTermsAndPolicyLoading: false,
  saveSupportInfoStatus: '',
};

export default (state: State = initialState, action: ActionT = {}): State =>
  produce(state, (draft) => {
    const payload = action.payload || {};

    // eslint-disable-next-line default-case
    switch (action.type) {
      case LOG_IN:
        draft.isLogIn = true;
        draft.loginError = initialState.loginError;
        return;
      case LOG_IN_SUCCESS:
        draft.permissions = defineUserPermissions(payload.data);
        draft.autoLogin = payload.autoLogin;
        draft.profile = payload.data;
        draft.isLogIn = false;
        draft.isFetching = false;
        draft.isAuthorized = true;
        draft.loginError = initialState.loginError;
        return;
      case LOG_IN_ERROR:
        draft.loginError = payload.response
          ? get(payload, 'response.data[0]', 'Some error has occurred. Please try again later.')
          : null;
        draft.isLogIn = false;
        draft.isAuthorized = false;
        draft.profile = initialState.profile;
        draft.permissions = initialState.permissions;
        return;
      case SIGN_UP:
        draft.isSignUp = true;
        draft.registerErrors = initialState.registerErrors;
        return;
      case SIGN_UP_SUCCESS:
        draft.profile = payload;
        draft.permissions = defineUserPermissions(payload);
        draft.autoLogin = true;
        draft.isSignUp = false;
        draft.isFetching = false;
        draft.isAuthorized = true;
        draft.registerErrors = initialState.registerErrors;
        return;
      case SIGN_UP_ERROR:
        draft.registerErrors = get(payload, 'response.data', 'Some error has occurred. Please try again later.');
        draft.isSignUp = false;
        draft.isAuthorized = false;
        draft.profile = initialState.profile;
        return;
      case SIGN_OUT:
      case SIGN_OUT_ERROR:
        draft.isSignUp = false;
        return;
      case SIGN_OUT_SUCCESS:
        draft.isFetching = false;
        draft.isAuthorized = false;
        draft.autoLogin = false;
        draft.profile = initialState.profile;
        draft.permissions = initialState.permissions;
        return;
      case UPDATE_USER:
      case AVATAR_UPLOAD:
      case REMOVE_AVATAR:
      case UPDATE_ORGANIZATION:
      case SEND_EMAIL_RESET:
      case RESET_PASSWORD:
      case CHECK_TOKEN:
        draft.isAuthorized = null;
        draft.isFetching = true;
        draft.passwordResetMessage = initialState.passwordResetMessage;
        draft.resetPasswordError = initialState.resetPasswordError;
        return;
      case CHECK_TOKEN_SUCCESS:
        draft.isFetching = false;
        draft.profile = {
          token: payload.token,
          ...payload.data,
        };
        draft.permissions = defineUserPermissions(payload.data);
        draft.autoLogin = payload.autoLogin;
        draft.isAuthorized = true;
        draft.fetchUserErrors = initialState.fetchUserErrors;
        return;
      case CHECK_TOKEN_FAILED:
        draft.isAuthorized = false;
        draft.autoLogin = false;
        draft.profile = initialState.profile;
        draft.isFetching = false;
        draft.loginError = initialState.loginError;
        draft.permissions = initialState.permissions;
        draft.registerErrors = initialState.registerErrors;
        draft.fetchUserErrors = defineServerErrors(payload, 'Some error has occurred. Please try again later.');
        draft.fetchUserErrors.forEach((error) => message.error(error, 10));
        return;
      case CANCEL_CHECK_TOKEN:
        draft.isAuthorized = false;
        draft.isFetching = false;
        draft.permissions = initialState.permissions;
        draft.registerErrors = initialState.registerErrors;
        return;
      case RESET_ERRORS:
        draft.registerErrors = initialState.registerErrors;
        draft.loginError = initialState.loginError;
        return;
      case UPDATE_USER_SUCCESS:
        draft.profile = {
          ...draft.profile,
          ...payload,
        };
        LocalStorageService.set('user', draft.profile);
        draft.updateUserError = initialState.registerErrors;
        draft.isFetching = false;
        message.success('Profile updated successfully.');
        return;
      case UPDATE_USER_FAILED:
        draft.isFetching = false;
        draft.updateUserError = defineServerErrors(payload, 'Some error has occurred. Please try again later.');
        draft.updateUserError.forEach((error) => message.error(error, 10));
        return;
      case UPDATE_ORGANIZATION_SUCCESS:
        draft.profile.organization = {
          ...draft.profile.organization,
          ...payload,
        };
        LocalStorageService.set('user', draft.profile);
        draft.updateOrganizationError = initialState.updateOrganizationError;
        draft.isFetching = false;
        message.success('Organization updated successfully.');
        return;
      case UPDATE_ORGANIZATION_FAILED:
        draft.isFetching = false;
        draft.updateOrganizationError = defineServerErrors(payload, 'Some error has occurred. Please try again later.');
        draft.updateOrganizationError.forEach((error) => message.error(error, 10));
        return;
      case SEND_EMAIL_RESET_SUCCESS:
        draft.isFetching = false;
        draft.passwordResetMessage = 'Email with further instructions have been sent to your email address.';
        draft.resetPasswordError = initialState.resetPasswordError;
        return;
      case SEND_EMAIL_RESET_FAILED:
        draft.isFetching = false;
        draft.resetPasswordError = defineServerErrors(payload, 'Some error has occurred. Please try again later.');
        return;
      case RESET_PASSWORD_SUCCESS:
        draft.isFetching = false;
        draft.resetPasswordData = payload;
        draft.passwordResetMessage = 'Password has been changed successfully. Check your email.';
        draft.resetPasswordError = initialState.resetPasswordError;
        return;
      case RESET_PASSWORD_FAILED:
        draft.isFetching = false;
        draft.resetPasswordError = defineServerErrors(payload, 'Some error has occurred. Please try again later.');
        return;
      case FETCH_ORGANIZATIONS:
        draft.isOrganizationsFetching = true;
        return;
      case FETCH_ORGANIZATIONS_SUCCESS:
        draft.organizations = payload.results;
        draft.isOrganizationsFetching = false;
        return;
      case FETCH_ORGANIZATIONS_FAILED: {
        draft.isOrganizationsFetching = false;
        const errors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        errors.forEach((error) => message.error(error, 10));
        return;
      }
      case SET_TERMS_POLICY_PAYLOAD:
        draft.termsPolicyPayload = payload;
        return;
      case ACCEPT_TERMS_AND_POLICY:
        draft.acceptTermsAndPolicyLoading = true;
        return;
      case ACCEPT_TERMS_AND_POLICY_SUCCESS:
        draft.acceptTermsAndPolicySuccess = true;
        draft.acceptTermsAndPolicyLoading = false;
        message.success('Terms and Policy Accepted.');
        return;
      case ACCEPT_TERMS_AND_POLICY_FAILED:
        draft.acceptTermsAndPolicySuccess = false;
        draft.acceptTermsAndPolicyLoading = false;
        return;
      case SAVE_SUPPORT_INFO:
        draft.isSaveSupportInfo = true;
        draft.saveSupportInfoStatus = '';
        return;
      case SAVE_SUPPORT_INFO_SUCCESS:
        draft.isSaveSupportInfo = false;
        draft.saveSupportInfoStatus = payload;
        draft.errors = initialState.errors;
        message.success('Issue submitted successfully.');
        return;
      case SAVE_SUPPORT_INFO_FAILED:
        draft.isSaveSupportInfo = false;
        draft.saveSupportInfoStatus = payload;
        draft.errors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.errors.forEach((error) => message.error(error, 10));
        return;
    }
  });
